<template>
  <div class="login-page">
    <VContainer fluid class="fill-height">
      <VRow align="center" justify="center">
        <VCol cols="12" sm="6" md="4" lg="3">
          <VCard elevation="2">
            <VCardTitle class="mb-4 login-title"> AQUALYS 2.0 </VCardTitle>
            <VCardText>
              <form @submit.prevent="login">
                <VTextField
                  v-model="username"
                  label="User ID"
                  :rules="rules.required"
                  :error-messages="usernameErrors"
                  @blur="validateUsername"
                  autofocus
                  density="comfortable"
                  variant="outlined"
                  class="mb-4"
                />

                <VTextField
                  v-model="password"
                  label="Password"
                  :type="showPassword ? 'text' : 'password'"
                  :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                  @click:append-inner="showPassword = !showPassword"
                  :rules="rules.required"
                  :error-messages="passwordErrors"
                  @blur="validatePassword"
                  density="comfortable"
                  variant="outlined"
                  class="mb-4"
                />

                <VCardActions>
                  <VBtn
                    type="submit"
                    color="primary"
                    :loading="callingAPI"
                    :disabled="callingAPI"
                    elevation="4"
                    block
                  >
                    Login
                  </VBtn>
                </VCardActions>
              </form>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </VContainer>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useSessionStore } from "@/store/session";
import { useAppStore } from "@/store/app";
import { storeToRefs } from "pinia";

const router = useRouter();
const sessionStore = useSessionStore();
const appStore = useAppStore();
const { callingAPI } = storeToRefs(sessionStore);

const username = ref("");
const password = ref("");
const showPassword = ref(false);

const rules = {
  required: [(v: string) => !!v || "Required"],
};

const usernameErrors = ref<string[]>([]);
const passwordErrors = ref<string[]>([]);

const validateUsername = () => {
  usernameErrors.value = rules.required
    .map((rule) => rule(username.value))
    .filter((result) => typeof result === "string") as string[];
};

const validatePassword = () => {
  passwordErrors.value = rules.required
    .map((rule) => rule(password.value))
    .filter((result) => typeof result === "string") as string[];
};

const gotoDashboard = () => router.push("/");

const login = async () => {
  validateUsername();
  validatePassword();
  if (usernameErrors.value.length || passwordErrors.value.length) {
    return;
  }

  try {
    const result = await sessionStore.signIn({
      username: username.value,
      password: password.value,
    });
    if (result?.success) {
      gotoDashboard();
    } else {
      appStore.sendErrorNotice("Login failed. Check your credentials.");
    }
  } catch (error) {
    appStore.sendErrorNotice("An error occurred during login.");
  }
};
</script>

<style scoped>
.fill-height {
  min-height: 100vh;
}

.login-title {
  background-color: rgb(var(--v-theme-primary));
  color: white;
  font-weight: 600;
  padding: 16px;
}

.mb-4 {
  margin-bottom: 1rem;
}

.login-page {
  min-height: 100vh;
  width: 100%;

  background-image: url("@/assets/login-bg.png");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
